import { Flex, Typography } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  RecordDetails,
  VisitsHistoryTab,
  ClientData,
  ClientCard,
  RecordHistory,
  RecordPayment,
  TechnologicalMap,
  MedicalCard,
} from '@app/components/RecordModal/components';
import {
  closeRecordModal,
  openRecordByIdInModal,
  restoreSavedClient,
  setHasChanges,
  setIsInitialized,
  setNextTab,
  setRecordTab,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { MenuTabEnum } from '@app/components/RecordModal/types';
import {
  getModalDataTestId,
  getWarningModalTitle,
} from '@app/components/RecordModal/utils';
import { EDIT_CLIENT_PATH } from '@app/constants';
import { useNewUnsavedData } from '@app/hooks';
import type { ConfirmResult } from '@app/hooks/useNewUnsavedData';
import { useConfirmNew } from '@app/providers/NewConfirmProvider/useConfirmNew';
import { useRecordModalContext } from '@app/providers/RecordModalProvider';
import type { Warnings } from '@app/services/rtkQuery/Records/types';
import { useAppDispatch, useAppSelector } from '@app/store';

import type { FC } from 'react';

const { Text } = Typography;

const EmptyComponent: FC = () => null;

export const useRecordModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { onConfirmRef } = useRecordModalContext();

  const {
    isOpen,
    tab,
    isLoading,
    hasChanges,
    mode,
    warnings: savedWarnings,
    isInitialized,
  } = useAppSelector(selectRecordModal);

  const { confirm: confirmUnsavedData } = useNewUnsavedData({
    hasChanges: hasChanges && isOpen && !isLoading && isInitialized,
    title: 'Несохранённые изменения',
    message:
      'Вы действительно хотите покинуть страницу, не сохранив изменения?',
    confirmText: 'Сохранить',
    cancelText: 'Отмена',
    discardText: 'Уйти без изменений',
    type: 'warning',
    dataTestId: 'unsaved-changes-popup',
  });

  const { show: showConfirm } = useConfirmNew();

  const confirmWarnings = useCallback(
    async (actualWarnings?: Warnings[]) => {
      const warnings = actualWarnings ?? savedWarnings;

      return new Promise<ConfirmResult>((resolve) => {
        showConfirm({
          title: getWarningModalTitle(warnings),
          message: (
            <Flex vertical gap={12}>
              {warnings.map(({ Message }) => (
                <Text key={Message} className="warning-message">
                  {Message}
                </Text>
              ))}
              <Text>
                Вы уверены что хотите{' '}
                {mode === 'edit' ? 'сохранить' : 'создать'} запись?
              </Text>
            </Flex>
          ),
          confirmText: 'Сохранить',
          cancelText: 'Вернуться к редактированию',
          showDiscard: false,
          onConfirm: () => resolve('confirm'),
          onCancel: () => resolve('cancel'),
          onDiscard: () => resolve('discard'),
          dataTestId: getModalDataTestId(warnings),
        });
      });
    },
    [savedWarnings, showConfirm, mode]
  );

  const onCancel = useCallback(async () => {
    const newParams = new URLSearchParams(searchParams);
    const isClientEditionPage = location.pathname.includes(EDIT_CLIENT_PATH);

    if (isClientEditionPage) {
      newParams.delete('source', 'client');
    }

    setSearchParams(newParams, { replace: true });

    if (hasChanges) {
      dispatch(setNextTab(null));
      const action = await confirmUnsavedData();

      switch (action) {
        case 'confirm':
          await onConfirmRef.current();
          break;
        case 'discard':
          dispatch(closeRecordModal());
          break;
        case 'cancel':
          break;
        default:
          break;
      }
    } else {
      dispatch(closeRecordModal());
    }
  }, [
    confirmUnsavedData,
    dispatch,
    hasChanges,
    location.pathname,
    onConfirmRef,
    setSearchParams,
    searchParams,
  ]);

  const onSelectTab = useCallback(
    async (key: MenuTabEnum) => {
      if (hasChanges) {
        dispatch(setNextTab(key));
        const action = await confirmUnsavedData();
        switch (action) {
          case 'confirm':
            await onConfirmRef.current();
            break;
          case 'discard':
            // Клиент, выбранный в «Поиск», не сохранён: вкладки показывают
            // клиента сохранённой записи (issue/11575).
            dispatch(restoreSavedClient());
            dispatch(setRecordTab(key));
            dispatch(setHasChanges(false));
            break;
          case 'cancel':
            break;
          default:
            break;
        }
      } else {
        dispatch(setRecordTab(key));
      }
    },
    [confirmUnsavedData, dispatch, hasChanges, onConfirmRef]
  );

  const getContent = useCallback(() => {
    if (!isOpen) {
      return EmptyComponent;
    }
    switch (tab) {
      case MenuTabEnum.RecordDetails:
        return RecordDetails;
      case MenuTabEnum.RecordPayment:
        return RecordPayment;
      case MenuTabEnum.RecordHistory:
        return RecordHistory;
      case MenuTabEnum.ClientData:
        return ClientData;
      case MenuTabEnum.ClientCard:
        return ClientCard;
      case MenuTabEnum.VisitsHistory:
        return VisitsHistoryTab;
      case MenuTabEnum.TechnologicalMap:
        return TechnologicalMap;
      case MenuTabEnum.MedicalCard:
        return MedicalCard;
      default:
        return EmptyComponent;
    }
  }, [tab, isOpen]);

  const Content = useMemo(getContent, [getContent]);

  const queryRecordId = useMemo(
    () => +(searchParams.get('recordId') ?? 0),
    [searchParams]
  );

  const queryRecordIdHandler = useCallback(() => {
    if (queryRecordId) {
      dispatch(openRecordByIdInModal(queryRecordId));
    }
  }, [dispatch, queryRecordId]);

  useEffect(() => {
    if (tab !== MenuTabEnum.RecordDetails && isInitialized) {
      dispatch(setIsInitialized(false));
      dispatch(setHasChanges(false));
    }
  }, [tab, dispatch, isInitialized]);

  return {
    isOpen,
    onCancel,
    onSelectTab,
    tab,
    Content,
    isLoading,
    queryRecordIdHandler,
    confirmUnsavedData,
    confirmWarnings,
  };
};
